import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks/useRootStore';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import styles from './imageOrganiserPopUp.module.scss';
import { IOrganiserPopUpImage } from 'stores/popUp/types';
import { SotableItem } from './SotableItem';
import { Button } from 'components/Core/Button';

export const ImageOrganiserPopUp = observer(function () {
  const {
    popUpStore: { organiserPopUpImages, title, callBack, closePopUp },
  } = useStore();

  const [active, setActive] = useState<IOrganiserPopUpImage | null>(null);
  const [items, setItems] = useState<IOrganiserPopUpImage[] | undefined>(organiserPopUpImages);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActive(items?.find((item) => item.id === active.id) || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (items && active && over && active.id !== over?.id) {
      setItems((prev) => {
        if (!prev) return prev;
        const oldIndex = prev.findIndex((item) => item.id === active.id);
        const newIndex = prev.findIndex((item) => item.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
    setActive(null);
  };

  const handleSave = () => {
    if (callBack && items) {
      callBack.cb(items.map((item, i) => ({ ...item, position: i + 1 })));
    } else {
      closePopUp();
    }
  };

  return (
    <>
      <p className={styles.title}>{title}</p>
      {items && items.length > 0 && (
        <div className={styles.content}>
          <div className={styles.imageBox}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}>
              <SortableContext items={items} strategy={rectSortingStrategy}>
                {items.map(({ imageUrl, id, name }) => (
                  <SotableItem key={id} id={id} imageUrl={imageUrl} isDragging={active?.id === id} name={name} />
                ))}
              </SortableContext>
              <DragOverlay>
                {active ? <SotableItem id={active.id} imageUrl={active.imageUrl} isActive name={active.name} /> : null}
              </DragOverlay>
            </DndContext>
          </div>
          <Button
            title='save'
            onClick={handleSave}
            classNameProp={styles.btn}
            isDisabled={!items || items.length === 0}
          />
        </div>
      )}
      {!items?.length && <p className={styles.noImagesText}>No images to display</p>}
    </>
  );
});
